// src/pages/admin/PaymentTransactions.jsx
import React, { useState, useEffect } from "react";
import {
  IconBellRinging,
  IconSearch,
  IconFilter,
  IconRefresh,
  IconChevronUp,
  IconChevronDown,
  IconCirclePlus,
  IconPencil,
  IconTrash,
  IconX,
  IconCheck,
} from "@tabler/icons-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "../../assets/images/paqtory.png";
import { useDispatch, useSelector } from "react-redux";
import { fetchPayments, searchAndFilterPayments } from "../../redux/slices/paymentsSlice";
import Sidebar from "../../components/common/AdminSideBar";
import Header from "../../components/common/AdminHeader";
import AdminLayout from "../../components/common/AdminLayout";
import { useAdminLayout } from "../../context/adminLayoutUseContext";
import LoadingFallback from "../../components/common/LoadingFallback";
import ErrorFallback from "../../components/common/ErrorFallback";

// Constants
const PAYMENT_STATUS = {
  PENDING: 'Pending',
  COMPLETED: 'Completed',
  FAILED: 'Failed'
};

const PAYMENT_METHODS = {
  STRIPE: 'Stripe',
  RAZORPAY: 'Razorpay',
  PAYPAL: 'PayPal',
  CARD: 'Credit Card'
};

// Reusable Components
function NavItem({ label, to }) {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      to={to}
      className={` px-3 py-2 rounded-md cursor-pointer flex items-center gap-2
        ${isActive
          ? "bg-[#FFD500] text-[#0B0C52] font-semibold"
          : "hover:bg-white/10 text-white"
        }`}
    >
      {label}
    </Link>
  );
}

function IconButton({ children, onClick }) {
  return (
    <button onClick={onClick} className="p-1 hover:bg-gray-100 rounded transition">
      {children}
    </button>
  );
}

function StatusCell({ status, positiveLabel }) {
  const isPositive = status?.toLowerCase() === positiveLabel.toLowerCase();
  return (
    <div className="inline-flex items-center gap-1">
      {isPositive && <IconCheck className="text-green-500" size={16} />}
      <span>{status}</span>
    </div>
  );
}

// Filter Modal Component
function FilterModal({ isOpen, onClose, filters, setFilters, onApply }) {
  const [localFilters, setLocalFilters] = useState({
    startDate: '',
    endDate: '',
    minPrice: '',
    maxPrice: '',
    status: [],
    paymentMethod: []
  });

  useEffect(() => {
    if (isOpen) {
      setLocalFilters({
        startDate: filters.startDate || '',
        endDate: filters.endDate || '',
        minPrice: filters.minPrice || '',
        maxPrice: filters.maxPrice || '',
        status: filters.status || [],
        paymentMethod: filters.paymentMethod || []
      });
    }
  }, [isOpen, filters]);

  const toggleStatus = (status) => {
    setLocalFilters(prev => ({
      ...prev,
      status: prev.status.includes(status)
        ? prev.status.filter(s => s !== status)
        : [...prev.status, status]
    }));
  };

  const togglePaymentMethod = (method) => {
    setLocalFilters(prev => ({
      ...prev,
      paymentMethod: prev.paymentMethod.includes(method)
        ? prev.paymentMethod.filter(m => m !== method)
        : [...prev.paymentMethod, method]
    }));
  };

  const handleApply = () => {
    setFilters({
      ...filters,
      ...localFilters
    });
    onApply();
    onClose();
  };

  const handleReset = () => {
    setLocalFilters({
      startDate: '',
      endDate: '',
      minPrice: '',
      maxPrice: '',
      status: [],
      paymentMethod: []
    });
    setFilters({
      ...filters,
      startDate: '',
      endDate: '',
      minPrice: '',
      maxPrice: '',
      status: [],
      paymentMethod: []
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-[500px] max-w-full rounded-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <IconX size={20} />
        </button>
        <h2 className="text-lg font-semibold text-[#0B0C52] mb-4">Filter Options</h2>

        <div className="mb-4">
          <h3 className="text-md font-semibold mb-2">Date Range</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-1">From</label>
              <input
                type="date"
                value={localFilters.startDate}
                onChange={(e) => setLocalFilters(prev => ({ ...prev, startDate: e.target.value }))}
                className="border border-gray-300 rounded px-2 py-1 w-full"
              />
            </div>
            <div>
              <label className="block text-sm mb-1">To</label>
              <input
                type="date"
                value={localFilters.endDate}
                onChange={(e) => setLocalFilters(prev => ({ ...prev, endDate: e.target.value }))}
                className="border border-gray-300 rounded px-2 py-1 w-full"
              />
            </div>
          </div>
        </div>

        <div className="mb-4">
          <h3 className="text-md font-semibold mb-2">Price Range</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-1">Min ($)</label>
              <input
                type="number"
                value={localFilters.minPrice}
                onChange={(e) => setLocalFilters(prev => ({ ...prev, minPrice: e.target.value }))}
                className="border border-gray-300 rounded px-2 py-1 w-full"
              />
            </div>
            <div>
              <label className="block text-sm mb-1">Max ($)</label>
              <input
                type="number"
                value={localFilters.maxPrice}
                onChange={(e) => setLocalFilters(prev => ({ ...prev, maxPrice: e.target.value }))}
                className="border border-gray-300 rounded px-2 py-1 w-full"
              />
            </div>
          </div>
        </div>

        <div className="mb-4">
          <h3 className="text-md font-semibold mb-2">Payment Status</h3>
          <div className="grid grid-cols-2 gap-2">
            {Object.values(PAYMENT_STATUS).map(status => (
              <label key={status} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={localFilters.status.includes(status)}
                  onChange={() => toggleStatus(status)}
                  className="rounded border-gray-300 text-[#0B0C52] focus:ring-[#0B0C52]"
                />
                <span>{status}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-md font-semibold mb-2">Payment Method</h3>
          <div className="grid grid-cols-2 gap-2">
            {Object.values(PAYMENT_METHODS).map(method => (
              <label key={method} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={localFilters.paymentMethod.includes(method)}
                  onChange={() => togglePaymentMethod(method)}
                  className="rounded border-gray-300 text-[#0B0C52] focus:ring-[#0B0C52]"
                />
                <span>{method}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button
            onClick={handleReset}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
          >
            Reset All
          </button>
          <button
            onClick={handleApply}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}

function PaymentTransactions() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setLayoutConfig } = useAdminLayout();

  const { loading, error, data: allPayments = [], isSearching } = useSelector((state) => state.payments);

  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [modalType, setModalType] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    minPrice: '',
    maxPrice: '',
    status: [],
    paymentMethod: []
  });

  // Pagination constants
  const pageSize = 10;

  // Fetch payments on mount
  useEffect(() => {
    setLayoutConfig({
      title: "Payment & Transactions",
      showBackButton: false,
      scrollable: false,
    });
    // dispatch(fetchPayments());
  }, [dispatch, setLayoutConfig]);

  // Debounced search and filter
  useEffect(() => {
    const timer = setTimeout(() => {
      const payload = {
        search_term: searchTerm,
        start_date: filters.startDate,
        end_date: filters.endDate,
        min_price: filters.minPrice,
        max_price: filters.maxPrice,
        status: filters.status,
        payment_method: filters.paymentMethod
      };

      if (searchTerm || filters.startDate || filters.endDate || filters.minPrice ||
        filters.maxPrice || filters.status.length > 0 || filters.paymentMethod.length > 0) {
        dispatch(searchAndFilterPayments(payload));
      } else {
        dispatch(fetchPayments());
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, filters, dispatch]);



  // Pagination calculations
  const totalCount = allPayments.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPayments = allPayments.slice(startIndex, endIndex);
  const fromCount = startIndex + 1;
  const toCount = endIndex > totalCount ? totalCount : endIndex;

  // Handlers
  const handleNextPage = () => currentPage < totalPages && setCurrentPage(prev => prev + 1);
  const handlePrevPage = () => currentPage > 1 && setCurrentPage(prev => prev - 1);

  const handleRefresh = () => {
    // dispatch(fetchPayments());
    setSearchTerm("");
    setFilters({
      startDate: '',
      endDate: '',
      minPrice: '',
      maxPrice: '',
      status: [],
      paymentMethod: []
    });
    setCurrentPage(1);
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
  };

  const togglePaymentSelection = (paymentId) => {
    setSelectedPayments(prev =>
      prev.includes(paymentId)
        ? prev.filter(id => id !== paymentId)
        : [...prev, paymentId]
    );
  };

  const selectAllPayments = () => {
    setSelectedPayments(prev =>
      prev.length === currentPayments.length
        ? []
        : currentPayments.map(payment => payment.id)
    );
  };

  const openModal = (type, payment = null) => {
    setSelectedPayment(payment);
    setModalType(type);
  };

  const closeModal = () => {
    setSelectedPayment(null);
    setModalType(null);
  };


  if (loading && !isSearching) {
    return <LoadingFallback />
  }

  if (error) {
    return <ErrorFallback error={error} onRetry={() => dispatch(fetchPayments())} />;
  }

  return (
    <div>
      <main className=" flex-1 bg-[#FFFFFF] text-gray-800 p-4 rounded">
        <div className=" rounded-md bg-[#FFFFFF] p-4">
          {/* Toolbar Row */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {selectedPayments.length > 0 ? (
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4 w-full">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-700 whitespace-nowrap">
                    {selectedPayments.length} selected
                  </span>


                  <div className="relative ml-1">
                    <input
                      type="text"
                      placeholder="Search By Transaction ID Or Order ID"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="
                                      border-2 border-[#463BC9] rounded text-sm px-2 py-1 pl-8 
                                      focus:outline-none focus:border-[3px] 
                                      peer
                                    "
                    />
                    <IconSearch
                      size={16}
                      className="absolute left-2 top-2 text-gray-400 peer-focus:text-[#463BC9]"
                    />
                  </div>



                </div>

                <div className="flex items-center gap-2">
                  <button
                    className="text-red-500 hover:text-red-700 flex items-center gap-1 text-sm px-3 py-1 rounded hover:bg-red-50"
                    onClick={() => {
                      // Handle bulk delete
                    }}
                  >
                    <IconTrash size={16} />
                    Delete
                  </button>

                  <button
                    className="text-gray-500 hover:text-gray-700 text-sm px-3 py-1 rounded hover:bg-gray-100"
                    onClick={() => setSelectedPayments([])}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Search Field */}
                <div className="relative ml-1">
                  <input
                    type="text"
                    placeholder="Search By Transaction ID Or Order ID"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="
                                      border-2 border-[#463BC9] rounded text-sm px-2 py-1 pl-8 
                                      focus:outline-none focus:border-[3px] 
                                      peer
                                    "
                  />
                  <IconSearch
                    size={16}
                    className="absolute left-2 top-2 text-gray-400 peer-focus:text-[#463BC9]"
                  />
                </div>

                {/* Pagination Label & Arrows */}
                <span className="text-sm text-gray-700 ml-auto mr-1">
                  {fromCount} - {toCount} of {totalCount}
                </span>
                <button
                  onClick={handlePrevPage}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <IconChevronUp size={18} />
                </button>
                <button
                  onClick={handleNextPage}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <IconChevronDown size={18} />
                </button>

                {/* Filter, Refresh */}
                <IconButton onClick={() => setIsFilterModalOpen(true)}>
                  <IconFilter size={18} />
                </IconButton>
                <IconButton onClick={handleRefresh}>
                  <IconRefresh size={18} />
                </IconButton>


              </>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto h-[calc(100vh-200px)]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#F9F9FF] z-20 shadow-md">
                <tr className="bg-[#F9F9FF] text-[#120E43]">
                  <th className="px-4 py-2 w-10">
                    <input
                      type="checkbox"
                      checked={selectedPayments.length > 0 && selectedPayments.length === currentPayments.length}
                      onChange={selectAllPayments}
                      ref={(input) => {
                        if (input) {
                          input.indeterminate =
                            selectedPayments.length > 0 &&
                            selectedPayments.length < currentPayments.length;
                        }
                      }}
                    />
                  </th>
                  <th className="px-4 py-2 text-left">Transaction ID</th>
                  <th className="px-4 py-2 text-left">Order ID</th>
                  <th className="px-4 py-2 text-left">Date</th>
                  <th className="px-4 py-2 text-left">Payment Method</th>
                  <th className="px-4 py-2 text-left">Amount</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentPayments.map((payment) => (
                  <tr
                    key={payment.id}
                    className="border-b border-gray-200 hover:bg-gray-50"
                  >
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        checked={selectedPayments.includes(payment.id)}
                        onChange={() => togglePaymentSelection(payment.id)}
                      />
                    </td>
                    <td className="px-4 py-2">{payment.transaction_id}</td>
                    <td className="px-4 py-2">#{payment.order_id}</td>
                    <td className="px-4 py-2">
                      {new Date(payment.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2">{payment.method}</td>
                    <td className="px-4 py-2">${payment.amount}</td>
                    <td className="px-4 py-2">
                      <StatusCell
                        status={payment.status}
                        positiveLabel="Completed"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <button
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          onClick={() => navigate(`/admin/payments/${payment.id}`)}
                        >
                          <IconPencil size={16} />
                        </button>
                        {/* <button
                            className="text-red-500 hover:text-red-700 flex items-center gap-1"
                            onClick={() => openModal("DELETE", payment)}
                          >
                            <IconTrash size={16} />
                          </button> */}
                      </div>
                    </td>
                  </tr>
                ))}
                {currentPayments.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-gray-500">
                      {isSearching ? 'No payments match your search criteria' : 'No payments found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* MODALS */}
      {modalType === "ADD" && <AddPaymentModal onClose={closeModal} />}
      {modalType === "EDIT" && selectedPayment && (
        <EditPaymentModal payment={selectedPayment} onClose={closeModal} />
      )}
      {modalType === "DELETE" && selectedPayment && (
        <DeletePaymentModal payment={selectedPayment} onClose={closeModal} />
      )}

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filters}
        setFilters={setFilters}
        onApply={handleApplyFilters}
      />
    </div>
  );
}

// MODAL COMPONENTS (keep the same as before)
function AddPaymentModal({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-[600px] max-w-full rounded-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <IconX size={20} />
        </button>

        <h2 className="text-lg font-semibold text-[#0B0C52] mb-4">
          Add New Payment
        </h2>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-semibold mb-1">
              Transaction ID
            </label>
            <input
              type="text"
              placeholder="#TXN2025..."
              className="border-2 border-[#463BC9] w-full rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">
              Order ID
            </label>
            <input
              type="text"
              placeholder="#ORD1024"
              className="border-2 border-[#463BC9] w-full rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Date</label>
            <input
              type="date"
              className="border-2 border-[#463BC9] w-full rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">
              Payment Method
            </label>
            <select className="border-2 border-[#463BC9] w-full rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[#463BC9]">
              <option>Razorpay</option>
              <option>PayPal</option>
              <option>Credit Card</option>
              <option>Stripe</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">
              Payment Amount
            </label>
            <input
              type="text"
              placeholder="$100.00"
              className="border-2 border-[#463BC9] w-full rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">
              Payment Status
            </label>
            <select className="border-2 border-[#463BC9] w-full rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[#463BC9]">
              <option>Paid</option>
              <option>Pending</option>
              <option>Failed</option>
            </select>
          </div>
        </div>

        <div className="flex gap-4">
          <button
            onClick={onClose}
            className="bg-[#0B0C52] text-white px-4 py-2 rounded hover:opacity-90"
          >
            Save
          </button>
          <button
            onClick={onClose}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function EditPaymentModal({ payment, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-[600px] max-w-full rounded-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <IconX size={20} />
        </button>

        <h2 className="text-lg font-semibold text-[#0B0C52] mb-4">
          Edit Payment
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Editing transaction: <strong>{payment.transactionId}</strong>
        </p>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-semibold mb-1">
              Transaction ID
            </label>
            <input
              type="text"
              defaultValue={payment.transactionId}
              className="border-2 border-[#463BC9] w-full rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">
              Order ID
            </label>
            <input
              type="text"
              defaultValue={payment.orderId}
              className="border-2 border-[#463BC9] w-full rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Date</label>
            <input
              type="date"
              className="border-2 border-[#463BC9] w-full rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">
              Payment Method
            </label>
            <select
              defaultValue={payment.method}
              className="border-2 border-[#463BC9] w-full rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            >
              <option>Razorpay</option>
              <option>PayPal</option>
              <option>Credit Card</option>
              <option>Stripe</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">
              Payment Amount
            </label>
            <input
              type="text"
              defaultValue={payment.amount}
              className="border-2 border-[#463BC9] w-full rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">
              Payment Status
            </label>
            <select
              defaultValue={payment.status}
              className="border-2 border-[#463BC9] w-full rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            >
              <option>Paid</option>
              <option>Pending</option>
              <option>Failed</option>
            </select>
          </div>
        </div>

        <div className="flex gap-4">
          <button
            onClick={onClose}
            className="bg-[#0B0C52] text-white px-4 py-2 rounded hover:opacity-90"
          >
            Update
          </button>
          <button
            onClick={onClose}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function DeletePaymentModal({ payment, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-[500px] max-w-full rounded-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <IconX size={20} />
        </button>

        <h2 className="text-lg font-semibold text-red-600 mb-4 flex items-center gap-2">
          <IconTrash size={20} />
          Delete Payment
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Are you sure you want to permanently delete{" "}
          <strong>{payment.transactionId}</strong>?
          <br />
          This action cannot be undone.
        </p>
        <div className="flex gap-4">
          <button
            onClick={onClose}
            className="bg-red-500 text-white px-4 py-2 rounded hover:opacity-90"
          >
            Delete
          </button>
          <button
            onClick={onClose}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default PaymentTransactions;